<template>

    <div
        class="canvas::editor-Admin bg-white flex flex-col"
        :class="{
            'fixed top-0 right-0 bottom-0 left-0 h-full fullscreen': fullscreen,
            'rounded-lg': !fullscreen,
        }">

        <div class="flex flex-col h-full w-full">

            <ToolbarTop
                :activeCanvasVersion="activeCanvasVersion"
                :canvasVersion="canvasVersion"
                :versionState="versionState"
                :activeMode="activeMode"
                :title="title"
                :instruction="instruction"
                :currentLanguageToShow="isMultiLanguage ? currentLanguage : null"
                :fullscreen.sync="fullscreen"/>

            <div class="canvasWrapper flex overflow-hidden">

                <canvas-component
                    ref="canvasComponent"
                    :primaryLanguage="primaryLanguage"
                    :language="language"
                    :canvasVersion="canvasVersion"
                    :activeMode="activeMode"
                    :activeViewport="activeViewport"
                    :stateSaving="stateSaving"
                    :galleryDraggingBlock="galleryDraggingBlock"
                    @loaded="iframeLoaded = true"/>

                <gallery
                    v-if="activeMode == 'overview'"
                    :canvasBlocksGalleryData="canvasBlocksGalleryData"
                    :canvasVersion="canvasVersion"
                    :canvasId="id"
                    @onDraggingBlock="galleryDraggingBlock = $event"
                    @addBlock="$refs['canvasComponent'].pageAddBlock($event)"
                />

            </div>

            <div class="text-right my-0 py-3 toolbarBottom" :class="{ loading: !iframeLoaded || stateSaving }">
                <div class="py-0 flex">

                    <ModeAndViewportToolbar
                        :activeMode.sync="activeMode"
                        :activeViewport.sync="activeViewport"/>

                    <VersionActions
                        :canvas-version="canvasVersion"
                        :version-state="versionState"
                        :useVersioning="useVersioning"
                        :currentLanguage="currentLanguage"
                        @update.canvasVersion="setNewLanguageCanvasVersion($event)"
                        @onNewCanvasVersions="setNewCanvasVersions($event)"
                        @canvasVersionBackToLive="canvasVersionBackToLive"/>

                </div>
            </div>

        </div>

    </div>

</template>

<script>

import Canvas from "./admin/Canvas";
import Gallery from "./admin/Gallery";
import VersionActions from "./admin/VersionActions";
import ModeAndViewportToolbar from "./admin/ModeAndViewportToolbar";
import ToolbarTop from "./admin/ToolbarTop";

export default {

    provide: function ()
    {
        return {
            editorComponent: this,
        };
    },

    components: {
        ModeAndViewportToolbar,
        'canvas-component': Canvas,
        Gallery,
        ToolbarTop,
        VersionActions,
    },

    props: {
        id: String,
        title: String,
        instruction: String,

        primaryLanguage: String,
        language: String,
        canvasVersions: Object,
        canvasBlocksGalleryData: Array,

        useVersioning: Boolean,
    },

    data()
    {
        return {
            activeMode: null,
            activeViewport: 'xl',
            canvasVersionsLocal: this.canvasVersions,
            localVersionState: null,
            iframeLoaded: false,
            stateSaving: false, // Manipulated else where.
            galleryDraggingBlock: null,
            currentLanguage: this.language || Object.keys(this.canvasVersions)[0],
            fullscreen: false,
        };
    },

    mounted()
    {
        this.initActiveVersion();
        this.initInstance();
        this.initVersionState();
    },

    methods: {

        initActiveVersion()
        {
            // To overview due to no canvas blocks present yet?
            this.activeMode = this.canvasVersion.canvas_blocks.length > 0 ? 'content' : 'overview';
        },

        initInstance()
        {
            // @ugly Set so language switcher can reach it.
            $(this.$el).data('vueInstance', this);
        },

        initVersionState()
        {
            // @ugly TODO Vue anti pattern.
            this.$watch('$refs.canvasComponent.$refs.iframe.iframeApp', () =>
            {
                // @ugly TODO Vue anti pattern.
                let iframeApp = this.$refs.canvasComponent.$refs.iframe.iframeApp;

                // Iframe app present? May be gone while iframe is loading for another language.
                if (iframeApp)
                    iframeApp.$watch('pageComponent.state.dirty', () =>
                    {
                        this.localVersionState = 'concept';
                    });
            });
        },

        setNewLanguageCanvasVersion(canvasVersion)
        {
            // Set
            this.canvasVersionsLocal[this.currentLanguage].canvasVersion = canvasVersion;
        },

        setNewCanvasVersions(canvasVersions)
        {
            // Set
            this.canvasVersionsLocal = canvasVersions;
        },

        canvasVersionBackToLive()
        {
            // Clear overwriteable version state. Computed this.versionState() will check what is right.
            this.localVersionState = null

            // TODO High: Refresh iframe.

        },

        scrollInViewport()
        {
            // Determine scrolltop.
            let scrollTop = $(this.$el).offset().top - 30;

            // Scroll
            $('html, body')
                .scrollTop(scrollTop);
        },

    },

    computed: {

        canvasComponent()
        {
            return this.$refs['canvasComponent'];
        },

        versionState()
        {
            // Overwrite?
            if (this.localVersionState)
                return this.localVersionState;

            // Check version.
            return this.canvasVersionsLocal[this.currentLanguage].activeCanvasVersion?.id && this.canvasVersion.created_at == this.canvasVersion.updated_at ? 'live' : 'concept';
        },

        canvasVersion()
        {
            return this.canvasVersionsLocal[this.currentLanguage].canvasVersion;
        },

        activeCanvasVersion()
        {
            return this.canvasVersionsLocal[this.currentLanguage].activeCanvasVersion;
        },

        isMultiLanguage()
        {
            return Object.values(this.canvasVersionsLocal).length > 1;
        },

    },

    watch: {

        'fullscreen'(newVal)
        {
            // Scroll due to fullscreen ended? We have to wait for the next redraw.
            if (!newVal)
                this.$nextTick(() => this.scrollInViewport());
        },

    }

}

</script>

<style lang="scss" scoped>

.canvas\:\:editor-Admin {
    @apply bg-white overflow-hidden shadow divide-y divide-gray-200;
    z-index: 100;

    //&:not(.fullscreen) {
    //    min-height: calc(100vh - 75px);
    //}

    .toolbarBottom {
        border-top: 1px solid #ddd !important;
        transition: opacity .2s;

        &.loading {
            opacity: .5;
            pointer-evens: none;
        }
    }

    .canvasWrapper {
        min-height: 440px;
        height: calc(100vh - 200px);
    }
}

</style>
